import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Avatar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ContactSupport as SupportIcon,
  Description as DocsIcon,
  VideoLibrary as VideoIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  QuestionAnswer as FaqIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Notifications as NotificationsIcon,
  ShoppingCart as ShoppingCartIcon,
  CloudDone as CloudDoneIcon
} from '@mui/icons-material';

const HelpPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const faqs = [
    {
      id: 'getting-started',
      question: t('hardcoded.pages_helppage.how_do_i_get_started'),
      answer: i18n.t('hardcoded.pages_helppage.start_by_adding_first_vehicle'),
      category: i18n.t('hardcoded.pages_helppage.getting_started')
    },
    {
      id: 'add-vehicle',
      question: t('hardcoded.pages_helppage.how_do_i_add_a'),
      answer: i18n.t('hardcoded.pages_helppage.go_to_vehicles_add_vehicle'),
      category: t('hardcoded.pages_helppage.vehicles')
    },
    {
      id: 'sensor-assignment',
      question: t('hardcoded.pages_helppage.how_do_i_assign_sensors'),
      answer: i18n.t('hardcoded.pages_helppage.in_vehicle_detail_click_assign'),
      category: t('hardcoded.contexts_tutorialcontext.sensors')
    },
    {
      id: 'alarm-configuration',
      question: t('hardcoded.pages_helppage.how_do_i_configure_pressure'),
      answer: i18n.t('hardcoded.pages_helppage.go_to_alarms_create_alarm'),
      category: t('hardcoded.pages_helppage.alarms')
    },
    {
      id: 'invite-users',
      question: i18n.t('hardcoded.pages_helppage.how_do_i_invite_new_users'),
      answer: i18n.t('hardcoded.pages_helppage.only_managers_admins_invite'),
      category: i18n.t('hardcoded.pages_helppage.users')
    },
    {
      id: 'telemetry-data',
      question: t('hardcoded.pages_helppage.how_do_i_view_real-time'),
      answer: t('hardcoded.pages_helppage.visit_the_telemetry_section_to'),
      category: t('hardcoded.pages_helppage.telemetry')
    },
    {
      id: 'export-data',
      question: t('hardcoded.pages_helppage.can_i_export_telemetry_data'),
      answer: i18n.t('hardcoded.pages_helppage.yes_go_to_telemetry_export'),
      category: t('hardcoded.pages_helppage.reports')
    },
    {
      id: 'vehicle-offline',
      question: i18n.t('hardcoded.pages_helppage.what_does_offline_mean'),
      answer: i18n.t('hardcoded.pages_helppage.vehicle_offline_24h_no_telemetry'),
      category: i18n.t('hardcoded.pages_helppage.troubleshooting')
    }
  ];

  const quickLinks = [
    {
      title: t('hardcoded.pages_helppage.user_guide'),
      description: t('hardcoded.pages_helppage.complete_guide_to_using_arsenal'),
      icon: <DocsIcon sx={{ fontSize: 40 }} />,
      action: i18n.t('hardcoded.pages_helppage.download_pdf'),
      color: '#1976d2'
    },
    {
      title: t('hardcoded.pages_helppage.video_tutorials'),
      description: i18n.t('hardcoded.pages_helppage.watch_step_by_step_tutorials'),
      icon: <VideoIcon sx={{ fontSize: 40 }} />,
      action: i18n.t('hardcoded.pages_helppage.watch_videos'),
      color: '#d32f2f'
    },
    {
      title: t('hardcoded.pages_helppage.api_documentation'),
      description: t('hardcoded.pages_helppage.integration_guide_for_developers'),
      icon: <DocsIcon sx={{ fontSize: 40 }} />,
      action: i18n.t('hardcoded.pages_helppage.view_docs'),
      color: '#388e3c'
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1 && !healthData) {
      fetchHealthData();
    }
  };

  const fetchHealthData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://aps.arsenal.mx/api/health');
      const data = await response.json();
      setHealthData(data);
    } catch (err) {
      setError('Failed to fetch health data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentTab === 1) {
      const interval = setInterval(fetchHealthData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [currentTab]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case t('hardcoded.pages_helppage.operational'):
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case t('hardcoded.pages_helppage.operational'):
        return <CheckCircleIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  return (
    <Box sx={{ bgcolor: '#fafafa', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            fontWeight={700} 
            gutterBottom
            sx={{ 
              fontSize: { xs: '2rem', md: '3rem' },
              letterSpacing: '-0.02em'
            }}
          >
{i18n.t('hardcoded.pages_helppage.help_and_support')}
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ fontWeight: 400, maxWidth: 600, mx: 'auto' }}
          >
            {i18n.t('hardcoded.pages_helppage.find_answers_get_support')}
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: 4 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'white'
            }}
          >
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              centered
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 2,
                  minHeight: 64
                }
              }}
            >
              <Tab label={i18n.t('hardcoded.pages_helppage.help_and_support')} />
              <Tab label={i18n.t('hardcoded.pages_helppage.status_web')} />
            </Tabs>
          </Paper>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 ? (
          <Grid container spacing={4}>
            {/* Left Column - Search and FAQs */}
            <Grid item xs={12} md={8}>
              {/* Search */}
              <Box sx={{ mb: 4 }}>
                <TextField
                  fullWidth
                  placeholder={i18n.t('hardcoded.pages_helppage.search_for_help')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: 'white',
                      fontSize: '1.1rem',
                      py: 0.5
                    }
                  }}
                />
              </Box>

              {/* FAQ Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  bgcolor: 'white'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
                    <FaqIcon />
                  </Avatar>
                  <Typography variant="h5" fontWeight={700}>
                    {i18n.t('hardcoded.pages_helppage.frequently_asked_questions')}</Typography>
                </Box>

                {filteredFaqs.length === 0 ? (
                  <Alert 
                    severity="info" 
                    sx={{ borderRadius: 2 }}
                  >
                    {t('hardcoded.pages_helppage.no_faqs_found_matching_your')}</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {filteredFaqs.map((faq) => (
                      <Accordion
                        key={faq.id}
                        expanded={expandedFaq === faq.id}
                        onChange={handleFaqChange(faq.id)}
                        elevation={0}
                        sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '12px !important',
                          '&:before': { display: 'none' },
                          overflow: 'hidden'
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ py: 1.5 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                            <Typography 
                              variant="subtitle1" 
                              fontWeight={600} 
                              sx={{ flex: 1 }}
                            >
                              {faq.question}
                            </Typography>
                            <Chip
                              label={faq.category}
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                bgcolor: 'primary.50',
                                color: 'primary.main'
                              }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                          <Typography 
                            variant="body1" 
                            color="text.secondary"
                            sx={{ lineHeight: 1.7 }}
                          >
                            {faq.answer}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right Column - Quick Links and Contact */}
            <Grid item xs={12} md={4}>
              {/* Quick Links */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  borderRadius: 3,
                  bgcolor: 'white'
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
{i18n.t('hardcoded.pages_helppage.quick_links')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {quickLinks.map((link, index) => (
                    <Card 
                      key={index} 
                      elevation={0}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: `${link.color}15`,
                              color: link.color,
                              width: 56,
                              height: 56,
                              mr: 2
                            }}
                          >
                            {link.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                              {link.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {link.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Button 
                          fullWidth 
                          variant="outlined"
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          {link.action}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>

              {/* Contact Support */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  bgcolor: 'white'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
                    <SupportIcon />
                  </Avatar>
                  <Typography variant="h6" fontWeight={700}>
                    {i18n.t('hardcoded.pages_helppage.contact_support')}</Typography>
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  sx={{ lineHeight: 1.7, mb: 3 }}
                >
                  {i18n.t('hardcoded.pages_helppage.cant_find_what_youre_looking')}
                </Typography>

                <List dense sx={{ mb: 2 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.email_support')}</Typography>}
                      secondary="aps@arsenal.mx"
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.phone_support')}</Typography>}
                      secondary="+52 81 8998 1837"
                    />
                  </ListItem>
                </List>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<EmailIcon />}
                  href="mailto:aps@arsenal.mx"
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    mb: 2
                  }}
                >
                  {i18n.t('hardcoded.pages_helppage.send_email')}</Button>

                <Alert 
                  severity="info" 
                  sx={{ borderRadius: 2 }}
                  icon={false}
                >
                  <Typography variant="caption" fontWeight={500}>
                    {i18n.t('hardcoded.pages_helppage.support_hours')}
                  </Typography>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          // Status Web Tab
          <Box>
            {loading && !healthData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={60} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {error}
              </Alert>
            ) : healthData ? (
              <Grid container spacing={3}>
                {/* Overall Status */}
                <Grid item xs={12}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 4, 
                      borderRadius: 3,
                      bgcolor: 'white',
                      textAlign: 'center'
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        mx: 'auto', 
                        mb: 2,
                        bgcolor: getStatusColor(healthData.status) === 'success' ? '#4caf50' : 
                                 getStatusColor(healthData.status) === 'warning' ? '#ff9800' : '#f44336'
                      }}
                    >
                      {getStatusIcon(healthData.status)}
                    </Avatar>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                      {healthData.service}
                    </Typography>
                    <Chip 
                      label={healthData.status.toUpperCase()}
                      color={getStatusColor(healthData.status)}
                      sx={{ 
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        px: 2,
                        py: 2.5,
                        height: 'auto'
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {i18n.t('hardcoded.pages_helppage.version')}{healthData.version} {i18n.t('hardcoded.pages_helppage.mode')}{healthData.sync_mode}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Core Services */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      bgcolor: 'white',
                      height: '100%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <CloudDoneIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight={700}>
                        {i18n.t('hardcoded.pages_helppage.core_services')}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 2
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.dynamodb')}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.connection')}
                          </Typography>
                        </Box>
                        <Chip 
                          label={healthData.dynamodb.connection}
                          color={getStatusColor(healthData.dynamodb.connection)}
                          size="small"
                        />
                      </Box>

                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 2
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.sync_status')}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.database_synchronization')}
                          </Typography>
                        </Box>
                        <Chip 
                          label={healthData.dynamodb.sync_status}
                          color={getStatusColor(healthData.dynamodb.sync_status)}
                          size="small"
                        />
                      </Box>

                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: 2
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.continuous_sync')}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.real_time_processing')}
                          </Typography>
                        </Box>
                        <Chip 
                          label={healthData.continuous_sync_service.running ? i18n.t('hardcoded.pages_helppage.running') : t('hardcoded.pages_helppage.stopped')}
                          color={healthData.continuous_sync_service.running ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Performance Metrics */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      bgcolor: 'white',
                      height: '100%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                        <SpeedIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight={700}>
{i18n.t('hardcoded.pages_helppage.performance')}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight={700} color="primary">
                            {healthData.performance.success_rate_percentage}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.success_rate')}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight={700} color="primary">
                            {healthData.performance.total_cycles_completed.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.cycles_completed')}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight={700} color="primary">
                            {healthData.performance.total_records_processed.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.records_processed')}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight={700} color="primary">
                            {healthData.performance.average_processing_time.toFixed(2)}ms
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.avg_processing_time')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Alarm System */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      bgcolor: 'white'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                        <NotificationsIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight={700}>
                        {i18n.t('hardcoded.pages_helppage.alarm_system')}</Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h3" fontWeight={700} color="error.main">
                            {healthData.alarm_system.critical_alarms}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.critical')}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h3" fontWeight={700} color="warning.main">
                            {healthData.alarm_system.warning_alarms}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.warning_status')}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h3" fontWeight={700} color="primary">
                            {healthData.alarm_system.active_profiles}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_helppage.profiles')}</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{i18n.t('hardcoded.pages_helppage.total_checks')}</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {healthData.alarm_system.metrics.total_alarm_checks.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{i18n.t('hardcoded.pages_helppage.emails_sent')}</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {healthData.alarm_system.metrics.emails_sent.toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Ticket & Ecommerce */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      bgcolor: 'white'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                        <ShoppingCartIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight={700}>
                        {i18n.t('hardcoded.pages_helppage.additional_systems')}</Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.ticket_system')}</Typography>
                        <Chip 
                          label={healthData.ticket_system.enabled ? i18n.t('hardcoded.pages_helppage.enabled') : i18n.t('hardcoded.pages_helppage.disabled')}
                          color={healthData.ticket_system.enabled ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight={700}>{healthData.ticket_system.total_tickets}</Typography>
                            <Typography variant="caption">{i18n.t('common.total')}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight={700}>{healthData.ticket_system.open_tickets}</Typography>
                            <Typography variant="caption">{i18n.t('hardcoded.pages_helppage.open_status')}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight={700}>{healthData.ticket_system.resolved_tickets}</Typography>
                            <Typography variant="caption">{i18n.t('hardcoded.pages_helppage.resolved')}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600}>{i18n.t('hardcoded.pages_helppage.e-commerce_system')}</Typography>
                        <Chip 
                          label={healthData.ecommerce_system.enabled ? i18n.t('hardcoded.pages_helppage.enabled') : i18n.t('hardcoded.pages_helppage.disabled')}
                          color={healthData.ecommerce_system.enabled ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight={700}>{healthData.ecommerce_system.total_carts}</Typography>
                            <Typography variant="caption">{i18n.t('hardcoded.pages_helppage.total_carts')}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight={700}>{healthData.ecommerce_system.active_carts}</Typography>
                            <Typography variant="caption">{i18n.t('hardcoded.pages_helppage.active_carts')}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>

                {/* Features */}
                <Grid item xs={12}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      bgcolor: 'white'
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                      {i18n.t('hardcoded.pages_helppage.active_features')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {healthData.features.map((feature, index) => (
                        <Chip
                          key={index}
                          label={feature.replace(/_/g, ' ')}
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                {/* Refresh Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={fetchHealthData}
                      disabled={loading}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4
                      }}
                    >
                      {loading ? <CircularProgress size={24} /> : i18n.t('hardcoded.pages_helppage.refresh_status')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : null}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default HelpPage;